import { Component, Story, Knobs } from './DocTypes';

const PlaygroundKnobs: Knobs = {
  value: {
    default: '',
    editor: 'textbox',
  },
  defaultValue: {
    default: '',
    editor: 'textbox',
  },
  name: {
    default: 'exampleName',
    editor: 'textbox',
  },
  placeholder: {
    default: 'Enter something here...',
    editor: 'textbox',
  },
  disabled: {
    default: false,
    editor: 'checkbox',
  },
  'colour-scheme': {
    editor: 'dropdown',
    editorOptions: ['light', 'dark', 'default', ''],
    default: '',
  },
  device: {
    editor: 'dropdown',
    editorOptions: ['mobile', 'desktop', ''],
    default: '',
  },
};

const PlaygroundKnobsV3: Knobs = {
  ...PlaygroundKnobs,
  'input-style': {
    default: '{"width": "200px"}',
    editor: 'textbox',
  },
};

const SimpleInput: Story = {
  title: 'Playground',
  description: 'A simple input element with various attributes and valueChanged event',
  coreComponent_v3: {
    html: `<tag3-input{{...knobs}}></tag3-input>`,
    knobs: PlaygroundKnobsV3,
  },
  reactWrapper_v3: {
    jsx: `<Tag3Input{{...knobs}} inputStyle={{width: "200px"}} />`,
    knobs: PlaygroundKnobs,
  },
};

const WithValidationAlert: Story = {
  title: 'With validation',
  description: 'A simple input element with various attributes',
  coreComponent_v3: {
    html: `<tag3-input {{...knobs}}></tag3-input>
<tag3-validation-alert type='warning'> <tag3-icon icon="QuestionMark" ></tag3-icon>
Sorry an error has occurred ... </tag3-validation-alert>`,
    knobs: PlaygroundKnobs,
  },
  reactWrapper_v3: {
    jsx: `<Example />`,
    parentJSX: `const Example = () => {
  return (
    <div>
      <Tag3Input {{...knobs}}></Tag3Input>
      <Tag3ValidationAlert type='warning'> <Tag3Icon icon="QuestionMark" />
      Sorry an error has occurred ... </Tag3ValidationAlert>
    </div>
  )
};`,
    knobs: PlaygroundKnobs,
  },
};
const WithInlineValidationAlert: Story = {
  title: 'With inline validation',
  description: 'A simple input element with various attributes',
  coreComponent_v3: {
    html: `<tag3-input {{...knobs}}></tag3-input>
<tag3-validation-alert-inline type='warning'> <tag3-icon icon="QuestionMark" ></tag3-icon>
Sorry an error has occurred ... </tag3-validation-alert-inline>`,
    knobs: PlaygroundKnobs,
  },
  reactWrapper_v3: {
    jsx: `<Example />`,
    parentJSX: `const Example = () => {
  return (
    <div>
      <Tag3Input {{...knobs}}></Tag3Input>
      <Tag3ValidationAlertInline type='warning'> <Tag3Icon icon="QuestionMark" />
      Sorry an error has occurred ... </Tag3ValidationAlertInline>
    </div>
  )
};`,
    knobs: PlaygroundKnobs,
  },
};

const RightIconStory: Story = {
  title: 'Right icon',
  description: 'A simple input element with right icon',
  coreComponent_v3: {
    html: `<div style="position: relative; display: inline-block;">
  <tag3-input placeholder="Enter an address"></tag3-input>
  <tag3-clickable style="position: absolute; right: 3px; top: 3px;">
    <tag3-icon-svg icon="ChevronRight" size="15"></tag3-icon-svg>
  </tag3-clickable>
</div>`,
    js: `
var clickable = document.querySelector('tag3-clickable');
clickable.addEventListener("buttonClick", function() {
  alert('You clicked the icon within the input.');
});
    `,
  },
  reactWrapper_v3: {
    jsx: `<div style={{position: 'relative', display: 'inline-block'}}>
  <Tag3Input placeholder="Enter an address" />
  <Tag3Clickable
    style={{position: 'absolute', right: '3px', top: '3px'}}
    onButtonClick={()=> alert('You clicked the icon within the input.')}
  >
    <Tag3IconSvg icon='ChevronRight' size='15' />
  </Tag3Clickable>
</div>`,
  },
};

export const TagInputDocs: Component = {
  type: 'component',
  name: 'input',
  description: 'Renders an input element',
  mobileFriendly: true,
  popular: true,
  preRelease: '',
  props: [
    {
      name: 'inputId',
      description: 'The id of the input',
      type: 'string',
    },
    {
      name: 'name',
      description: 'The name of the input',
      type: 'string',
    },
    {
      name: 'type',
      description: 'The type of the input',
      type: 'string',
    },
    {
      name: 'value',
      description: 'The value of the input',
      type: 'string',
    },
    {
      name: 'defaultValue',
      description: 'The default value of the input',
      type: 'string',
    },
    {
      name: 'placeholder',
      description: 'The placeholder of the input',
      type: 'string',
    },
    {
      name: 'disabled',
      description: 'disabled',
      type: 'boolean',
    },
    {
      name: 'autocomplete',
      description: 'autocomplete',
      type: 'string',
    },
    {
      name: 'maxLength',
      description: 'maximum number of characters accepted',
      type: 'number',
    },
    {
      name: 'inputRef',
      description:
        'Input ref when it is used with React Hook Form. A demo can be find in tag-docs/Forms',
      type: 'any',
    },
    {
      name: 'inputStyle',
      description: 'JSX style object in JSON to apply to the text',
      type: 'string',
    },
    {
      name: 'colour-scheme',
      description:
        'V3 Only: Dark mode (theme) control. Default for normal browser driven control. Dark | Light to force respective styling regardless of browser setting.',
      type: 'dark | light | default',
      default: 'light',
    },
    {
      name: 'device',
      description: 'V3 Only: set preferred styling theme for device type.',
      type: 'desktop | mobile',
      default: 'desktop',
    },
  ],
  reactPropOverrides: [],
  events: [
    {
      name: 'inputValueChange',
      description: 'Event raised when the value changes',
      parameters: [
        {
          name: 'name',
          type: 'string',
          description: 'input name',
        },
        {
          name: 'value',
          type: 'string',
          description: 'input value',
        },
      ],
    },
    {
      name: 'inputBlur',
      description: 'Event raised when the input loses focus',
      parameters: [
        {
          name: 'name',
          type: 'string',
          description: 'input name',
        },
      ],
    },
    {
      name: 'inputFocus',
      description: 'Event raised when the input gains focus',
      parameters: [
        {
          name: 'name',
          type: 'string',
          description: 'input name',
        },
      ],
    },
  ],
  methods: [],
  stories: [SimpleInput, WithValidationAlert, WithInlineValidationAlert, RightIconStory],
};
